/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import React, { useContext, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { Subscribe } from 'unstated';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { COMMERCE_BACKEND_CCP } from '@atlassian/commerce-ui/rollout';
import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceAbort,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TRIAL_COMPONENTS_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { useCommerceBackendName } from '../useCommerceBackendName.ts';
import { useIsPreDunningPeriod } from '../useIsPreDunningPeriod.ts';
import { getLicenseConsumingUserCountFromQuery } from '../graphql/getLicenseConsumingUserCountFromQuery.ts';
import { useBillingUrls } from '../useBillingUrls.ts';
import { useHasBillingInformation } from '../useHasBillingInformation.ts';
import {
	useIsEligibleForPremiumReverseTrialsExperiment,
	usePremiumReverseTrialABCohort,
} from '../PremiumReverseTrialsExperiment/useIsEligibleForPremiumReverseTrialsExperiment.ts';
import { useReverseTrialCohort } from '../PremiumReverseTrialsExperiment/useReverseTrialEligible.tsx';
import { usePlanSelectionPageInfo } from '../PremiumReverseTrialsExperiment/usePlanSelectionPageInfo.ts';
import { useAutoUpgradeNotATrialExperiment } from '../AutoUpgradeNotATrialExperiment/useAutoUpgradeNotATrialExperiment.ts';
import { getDeactivationDate } from '../utils/getDeactivationDate.ts';

import type { GracePeriodBannerTrialDetailsQuery as GracePeriodBannerTrialDetailsQuery$data } from './__types__/GracePeriodBannerTrialDetailsQuery.ts';
import { NonCompliantBannerMessage } from './NonCompliantBannerMessage.tsx';
// eslint-disable-next-line import/no-cycle
import { CompliantBannerMessage } from './CompliantBannerMessage.tsx';
import { AutoUpgradeAvoidDeactivationBanner } from './AutoUpgradeAvoidDeactivationBanner.tsx';

const BANNER_NAME = 'GracePeriodBanner';
export const NON_COMPLIANT_BANNER_HEIGHT = 56;
export const COMPLIANT_BANNER_HEIGHT = 48;
const FREE_USER_LIMIT = 10;

export const BannerComponent = () => {
	const { isLicensed, isLoggedIn, userId, edition, cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const isTrialEdition =
		edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM;

	const initialSkipConditions =
		!isLicensed || !isLoggedIn || !userId || !isTrialEdition || !isSiteAdmin;

	useEffect(() => {
		if (!initialSkipConditions) {
			experienceTracker.start({
				name: ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE,
			});
		}
	}, [experienceTracker, initialSkipConditions]);

	const {
		commerceBackendName,
		loading: commerceBackendNameLoading,
		error: commerceBackendNameError,
	} = useCommerceBackendName(initialSkipConditions);

	const canSkipTrialDetails = initialSkipConditions || commerceBackendName !== COMMERCE_BACKEND_CCP;

	const {
		data,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_BANNER_DISMISSED,
		// skip conditions
		canSkipTrialDetails,
	);
	const deserializedState = deserializeState(data);
	const { trialGracePeriodBannerDismissed } = deserializedState;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialError,
	} = useQuery<GracePeriodBannerTrialDetailsQuery$data>(
		graphql`
			query GracePeriodBannerTrialDetailsQuery {
				license {
					billingSourceSystem
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: canSkipTrialDetails,
		},
	);

	const licenseConsumingUserCount = getLicenseConsumingUserCountFromQuery(trialDetailsData);
	const numberOfUsersOverFreeLimit = licenseConsumingUserCount - FREE_USER_LIMIT;
	const isOverFreeLimit = licenseConsumingUserCount > FREE_USER_LIMIT;
	const trialEndDate = trialDetailsData?.license?.trialEndDate;
	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(trialEndDate) ?? null;

	const isInTrial = !!trialEndDate && trialDaysLeft !== null && trialDaysLeft > 0;

	const {
		deactivationDate: preDunningDeactivationDate,
		isPreDunningPeriod,
		loading: predunningLoading,
		error: predunningError,
	} = useIsPreDunningPeriod({ skip: canSkipTrialDetails });

	let deactivationDate;

	if (isPreDunningPeriod) {
		deactivationDate = preDunningDeactivationDate;
	} else {
		deactivationDate = trialEndDate && getDeactivationDate({ trialEndDate });
	}

	const {
		hasBillingInformation,
		loading: billingLoading,
		error: billingErr,
	} = useHasBillingInformation(!deactivationDate);

	const shouldSkipPremiumReverseTrialsCheck =
		canSkipTrialDetails ||
		edition !== ConfluenceEdition.PREMIUM ||
		hasBillingInformation ||
		!fg('growth_confluence_buy_reverse_trials_fg');

	const {
		isReverseTrialer,
		loading: isReverseTrialLoading,
		error: isReverseTrialerError,
	} = useReverseTrialCohort({
		skip: shouldSkipPremiumReverseTrialsCheck,
	});

	const {
		isAvailableToUser: isPlanSelectionPageUrlAvailable,
		planSelectionPageUrl,
		error: pspUrlError,
		loading: isPSPUrlLoading,
	} = usePlanSelectionPageInfo({
		skip: shouldSkipPremiumReverseTrialsCheck,
	});

	const isEligibleForPremiumReverseTrialsExperiment =
		useIsEligibleForPremiumReverseTrialsExperiment({
			isReverseTrialer,
			isInPredunning: isPreDunningPeriod,
			edition,
			hasBillingInformation,
			commerceBackendName,
			isPlanSelectionPageUrlAvailable,
		});

	const premiumReverseTrialABCohort = usePremiumReverseTrialABCohort({
		isEligible: isEligibleForPremiumReverseTrialsExperiment,
		touchpointId: 'grace-period-banner',
		attributes: {
			licenseConsumingUserCount,
			deactivationDate,
		},
	});

	const {
		loading: useAutoUpgradeNotATrialExperimentLoading,
		error: useAutoUpgradeNotATrialExperimentError,
		autoUpgradeNotATrialExperimentCohort,
	} = useAutoUpgradeNotATrialExperiment({
		isSiteAdmin,
		edition,
		hasBillingInformation,
		isInTrialOrPredunning: isInTrial || isPreDunningPeriod,
		commerceBackendName,
		source: 'confluence_grace_period_banner',
		skip: canSkipTrialDetails || !fg('auto_upgrade_not_a_trial_experiment_connie_gate'),
		analyticAttributes: { trialDaysLeft },
	});

	const canSkipBillingUrl = !isPreDunningPeriod || !deactivationDate;
	const { paymentFlowUrl, loading: urlLoading, error: urlErr } = useBillingUrls(canSkipBillingUrl);

	const bannerType: string =
		licenseConsumingUserCount > FREE_USER_LIMIT ? 'non-compliant' : 'compliant';

	// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
	// Permission error in this case is expected and FE should handle it gracefully - "markErrorAsHandled" and abort the experience instead of reporting as failures.
	if (trialError && isUnauthorizedError(trialError)) {
		markErrorAsHandled(trialError);
		return (
			<ExperienceAbort
				name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
				reason="Aborted due to insufficient permission"
			/>
		);
	}

	// the queries are dependent and only one error should occur at a time
	const error =
		commerceBackendNameError ||
		onboardingStateError ||
		trialError ||
		predunningError ||
		urlErr ||
		billingErr ||
		isReverseTrialerError ||
		pspUrlError ||
		useAutoUpgradeNotATrialExperimentError;

	if (error) {
		return (
			<ExperienceFailure
				name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
				error={error}
				attributes={{
					isSiteAdmin,
					commerceBackendName,
					licenseConsumingUserCount,
					isPreDunningPeriod,
					bannerType,
					daysLeftUntilDeactivation: preDunningDeactivationDate?.daysLeftUntilDeactivation,
					trialDaysLeft,
				}}
			/>
		);
	}

	const isLoading =
		commerceBackendNameLoading ||
		trialLoading ||
		predunningLoading ||
		urlLoading ||
		onboardingStateLoading ||
		isReverseTrialLoading ||
		billingLoading ||
		isPSPUrlLoading ||
		useAutoUpgradeNotATrialExperimentLoading;

	if (
		canSkipTrialDetails ||
		isLoading ||
		!premiumReverseTrialABCohort ||
		// Auto Upgrade Not a Trial Experiment Grace Period Banner is shown to sites that are in trial and in pre-dunning
		(!isPreDunningPeriod && autoUpgradeNotATrialExperimentCohort !== 'treatment') ||
		!deactivationDate ||
		!paymentFlowUrl ||
		// compliant banner dismissal
		(!isOverFreeLimit && trialGracePeriodBannerDismissed)
	) {
		return null;
	}

	return (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				const bannerHeight = isOverFreeLimit
					? NON_COMPLIANT_BANNER_HEIGHT
					: COMPLIANT_BANNER_HEIGHT;

				bannerState?.show(BANNER_NAME, bannerHeight);
				addUFOCustomData({
					isGracePeriodBannerDisplayed: true,
				});

				if (autoUpgradeNotATrialExperimentCohort === 'treatment') {
					return (
						<>
							<AutoUpgradeAvoidDeactivationBanner cloudId={cloudId} />
							<ExperienceSuccess
								name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
								attributes={{
									bannerType,
									autoUpgradeNotATrialExperimentCohort,
									trialDaysLeft,
									daysLeftUntilDeactivation: preDunningDeactivationDate?.daysLeftUntilDeactivation,
								}}
							/>
						</>
					);
				}

				return (
					<>
						{isOverFreeLimit ? (
							<NonCompliantBannerMessage
								linkToAddBilling={paymentFlowUrl}
								deactivationDate={deactivationDate.dateString}
								numberOfUsersOverFreeLimit={numberOfUsersOverFreeLimit}
								licenseConsumingUserCount={licenseConsumingUserCount}
								edition={edition}
								premiumReverseTrialABCohort={premiumReverseTrialABCohort}
								linkToChoosePlan={planSelectionPageUrl}
							/>
						) : (
							<CompliantBannerMessage
								linkToAddBilling={paymentFlowUrl}
								daysLeftUntilDeactivation={preDunningDeactivationDate?.daysLeftUntilDeactivation}
								licenseConsumingUserCount={licenseConsumingUserCount}
								edition={edition}
								hideBanner={() => bannerState?.hide(BANNER_NAME)}
								premiumReverseTrialABCohort={premiumReverseTrialABCohort}
								linkToChoosePlan={planSelectionPageUrl}
							/>
						)}
						<ExperienceSuccess
							name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
							attributes={{
								bannerType,
								autoUpgradeNotATrialExperimentCohort,
								daysLeftUntilDeactivation: preDunningDeactivationDate?.daysLeftUntilDeactivation,
								trialDaysLeft,
							}}
						/>
					</>
				);
			}}
		</Subscribe>
	);
};

export const GracePeriodBanner = () => {
	return (
		<TransparentErrorBoundary attribution={Attribution.GROWTH_LUNA}>
			<BannerComponent />
		</TransparentErrorBoundary>
	);
};
